/**
 * FusionMacroBoard — Daily macro compass for Keto Fusion.
 * Shows target vs consumed with training/non-training awareness.
 *
 * Props:
 *   target: { target_kcal, macros: {carbs_g, protein_g, fat_g}, is_training_day, notes, meals_suggestion }
 *   consumed: { kcal, carbs_g, protein_g, fat_g }
 *   phase: current phase object
 */

import React from "react";
import { motion } from "framer-motion";
import { Flame, Dumbbell, Beef, Droplet, Wheat, Utensils } from "lucide-react";

const MACRO_META = {
  carbs: { label: "Carbs", color: "#84cc16", Icon: Wheat, unit: "g" },
  protein: { label: "Proteína", color: "#f97316", Icon: Beef, unit: "g" },
  fat: { label: "Gordura", color: "#a855f7", Icon: Droplet, unit: "g" },
};

function MacroBar({ macro, consumed, target, color, Icon, overLimit }) {
  const pct = Math.min(150, Math.round(((consumed || 0) / Math.max(1, target)) * 100));
  const barColor = overLimit ? "#ef4444" : color;
  return (
    <div className="mb-3" data-testid={`macro-bar-${macro}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: barColor }} />
        <span className="text-[11px] uppercase tracking-wider text-zinc-400 flex-1">{MACRO_META[macro].label}</span>
        <span className="text-xs font-mono" style={{ color: barColor }}>
          {Math.round(consumed || 0)}<span className="text-zinc-600"> / {target}g</span>
        </span>
      </div>
      <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: barColor, boxShadow: `0 0 8px ${barColor}80` }}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, pct)}%` }}
          transition={{ duration: 1 }}
        />
      </div>
      {overLimit && (
        <p className="text-[10px] text-red-400 mt-1">⚠️ Excedeu o limite da fase</p>
      )}
    </div>
  );
}

export default function FusionMacroBoard({ target, consumed = {}, phase }) {
  if (!target) return null;
  const kcalPct = Math.min(150, Math.round(((consumed.kcal || 0) / Math.max(1, target.target_kcal)) * 100));
  const delta = target.delta_from_tdee || 0;
  const deltaLabel = delta > 0 ? `+${delta} superávit` : delta < 0 ? `${delta} défice` : "TDEE manutenção";
  const deltaColor = delta > 0 ? "#84cc16" : delta < 0 ? "#f97316" : "#a1a1aa";

  return (
    <div
      className="rounded-2xl border bg-zinc-950/70 p-5"
      style={{ borderColor: `${phase?.color || "#84cc16"}30` }}
      data-testid="fusion-macro-board"
    >
      {/* Header — training day badge */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Objectivo do Dia</p>
          <h3 className="text-lg font-bold text-zinc-100">
            {target.is_training_day ? "🏋️ Dia de Treino" : "🌿 Dia de Recuperação"}
          </h3>
        </div>
        <div
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest"
          style={{ background: `${deltaColor}15`, color: deltaColor, border: `1px solid ${deltaColor}40` }}
        >
          {deltaLabel}
        </div>
      </div>

      {/* Kcal ring */}
      <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-zinc-900/60">
        <div className="relative w-16 h-16">
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="26" fill="none" stroke="#27272a" strokeWidth="6" />
            <motion.circle
              cx="32" cy="32" r="26" fill="none"
              stroke={kcalPct > 110 ? "#ef4444" : "#00ff88"}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${(Math.min(100, kcalPct) / 100) * 163} 163`}
              transform="rotate(-90 32 32)"
              initial={{ strokeDasharray: "0 163" }}
              animate={{ strokeDasharray: `${(Math.min(100, kcalPct) / 100) * 163} 163` }}
              transition={{ duration: 1.2 }}
              style={{ filter: "drop-shadow(0 0 4px #00ff8880)" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Flame className="w-4 h-4 text-emerald-400" />
          </div>
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Calorias</p>
          <p className="text-xl font-bold text-zinc-100">
            {Math.round(consumed.kcal || 0)}
            <span className="text-sm text-zinc-500 font-mono"> / {target.target_kcal} kcal</span>
          </p>
          <p className="text-[10px] text-zinc-500">TDEE {target.tdee} · BMR {target.bmr}</p>
        </div>
      </div>

      {/* Macros bars */}
      <MacroBar
        macro="carbs"
        consumed={consumed.carbs_g}
        target={target.macros.carbs_g}
        color={MACRO_META.carbs.color}
        Icon={MACRO_META.carbs.Icon}
        overLimit={(consumed.carbs_g || 0) > target.macros.carbs_g}
      />
      <MacroBar
        macro="protein"
        consumed={consumed.protein_g}
        target={target.macros.protein_g}
        color={MACRO_META.protein.color}
        Icon={MACRO_META.protein.Icon}
      />
      <MacroBar
        macro="fat"
        consumed={consumed.fat_g}
        target={target.macros.fat_g}
        color={MACRO_META.fat.color}
        Icon={MACRO_META.fat.Icon}
      />

      {/* Phase note */}
      {target.notes && (
        <div className="mt-3 p-2.5 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <p className="text-[11px] text-zinc-400 leading-snug">
            <span style={{ color: phase?.color }}>💡</span> {target.notes}
          </p>
        </div>
      )}

      {/* Meals suggestion */}
      {target.meals_suggestion && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            <Utensils className="w-3 h-3" /> Distribuição sugerida
          </p>
          <div className="space-y-2">
            {target.meals_suggestion.map((m, i) => (
              <div
                key={i}
                className="rounded-lg bg-zinc-900/40 border border-zinc-800 px-3 py-2"
                data-testid={`meal-suggestion-${i}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-zinc-200 truncate">
                    {m.name}
                  </span>
                  <span className="text-[10px] font-mono text-emerald-400 whitespace-nowrap flex-shrink-0">
                    ~{Math.round((target.target_kcal * m.kcal_pct) / 100)} kcal
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 leading-snug">{m.focus}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
